import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import { 
  PlusIcon, 
  ChartBarIcon, 
  EllipsisVerticalIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import toast from 'react-hot-toast';

export default function DashboardList() {
  const { dashboards, isLoading, fetchDashboards, deleteDashboard, createDashboard } = useDashboardStore();
  const { isSuperUser, getCurrentCompany } = useAuthStore();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  
  const currentCompany = getCurrentCompany();
  const needsCompanySelection = isSuperUser() && !currentCompany;
  
  useEffect(() => {
    if (!needsCompanySelection) {
      fetchDashboards();
    }
  }, [fetchDashboards, needsCompanySelection, currentCompany]);
  
  const filteredDashboards = dashboards.filter(d => 
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.description?.toLowerCase().includes(search.toLowerCase())
  );
  
  const handleCreateDashboard = async () => {
    if (needsCompanySelection) {
      toast.error('Please select a company first');
      navigate('/admin/companies');
      return;
    }
    
    try {
      const dashboard = await createDashboard({
        name: 'Untitled Dashboard',
        description: ''
      });
      navigate(`/dashboards/${dashboard._id}`);
    } catch (error) {
      toast.error(error.message || 'Failed to create dashboard');
    }
  };
  
  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this dashboard?')) {
      try {
        await deleteDashboard(id);
        toast.success('Dashboard deleted');
      } catch (error) {
        toast.error('Failed to delete dashboard');
      }
    }
  };
  
  // Show company selection prompt for Super Admins
  if (needsCompanySelection) {
    return (
      <div className="animate-fade-in">
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <BuildingOffice2Icon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            Select a Company
          </h3>
          <p className="text-surface-500 dark:text-surface-400 mb-6 max-w-md mx-auto">
            As a Super Admin, you need to select a company to view and manage its dashboards.
          </p>
          <button onClick={() => navigate('/admin/companies')} className="btn-primary">
            <BuildingOffice2Icon className="w-5 h-5 mr-2" />
            Select Company
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      {/* Current company banner for Super Admins */}
      {isSuperUser() && currentCompany && (
        <div className="mb-6 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuildingOffice2Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <span className="text-primary-900 dark:text-primary-100">
              Viewing dashboards for <strong>{currentCompany.name}</strong>
            </span>
          </div>
          <button 
            onClick={() => navigate('/admin/companies')} 
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Switch Company
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
            Dashboards
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">
            Create and manage your data visualizations
          </p>
        </div>
        <button onClick={handleCreateDashboard} className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          New Dashboard
        </button>
      </div>
      
      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dashboards..."
            className="input pl-10"
          />
        </div>
        <button className="btn-secondary">
          <FunnelIcon className="w-5 h-5 mr-2" />
          Filters
        </button>
      </div>
      
      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="card p-6">
              <div className="skeleton h-6 w-3/4 mb-4" />
              <div className="skeleton h-4 w-full mb-2" />
              <div className="skeleton h-4 w-2/3" />
            </div>
          ))}
        </div>
      )}
      
      {/* Empty state */}
      {!isLoading && filteredDashboards.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center">
            <ChartBarIcon className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            {search ? 'No dashboards found' : 'No dashboards yet'}
          </h3>
          <p className="text-surface-500 dark:text-surface-400 mb-6">
            {search 
              ? 'Try adjusting your search terms'
              : 'Create your first dashboard to start visualizing your data'}
          </p>
          {!search && (
            <button onClick={handleCreateDashboard} className="btn-primary">
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Dashboard
            </button>
          )}
        </div>
      )}
      
      {/* Dashboard grid */}
      {!isLoading && filteredDashboards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDashboards.map((dashboard, index) => (
            <Link
              key={dashboard._id}
              to={`/dashboards/${dashboard._id}`}
              className="card-hover p-6 group"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <ChartBarIcon className="w-6 h-6 text-white" />
                </div>
                
                <Menu as="div" className="relative">
                  <Menu.Button 
                    onClick={(e) => e.preventDefault()}
                    className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-surface-100 dark:hover:bg-surface-800 transition-all"
                  >
                    <EllipsisVerticalIcon className="w-5 h-5 text-surface-500" />
                  </Menu.Button>
                  <Menu.Items className="absolute right-0 mt-1 w-48 bg-white dark:bg-surface-800 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 z-10">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/dashboards/${dashboard._id}`);
                          }}
                          className={`${active ? 'bg-surface-100 dark:bg-surface-700' : ''} flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-200`}
                        >
                          <EyeIcon className="w-4 h-4" />
                          View
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/dashboards/${dashboard._id}`);
                          }}
                          className={`${active ? 'bg-surface-100 dark:bg-surface-700' : ''} flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-700 dark:text-surface-200`}
                        >
                          <PencilIcon className="w-4 h-4" />
                          Edit
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => handleDelete(dashboard._id, e)}
                          className={`${active ? 'bg-red-50 dark:bg-red-950' : ''} flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600`}
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Menu>
              </div>
              
              <h3 className="font-medium text-surface-900 dark:text-white mb-1 truncate">
                {dashboard.name}
              </h3>
              <p className="text-sm text-surface-500 dark:text-surface-400 line-clamp-2 mb-4">
                {dashboard.description || 'No description'}
              </p>
              
              <div className="flex items-center justify-between text-xs text-surface-400">
                <span>{dashboard.chartCount || 0} charts</span>
                <span>{new Date(dashboard.updatedAt).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

